import { useState } from 'react';
import { Clock, Mail, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import type { IAiReplySettings } from '@/@types/aiReply';

interface IAiReplyFollowUpSettingsProps {
  settings: IAiReplySettings | null | undefined;
  onUpdate: (updates: {
    followUpEnabled?: boolean;
    maxFollowUps?: number;
    gmailLabelsEnabled?: boolean;
  }) => Promise<void>;
}

export function AiReplyFollowUpSettings({ settings, onUpdate }: IAiReplyFollowUpSettingsProps) {
  const [maxFollowUpsInput, setMaxFollowUpsInput] = useState<string>('');

  if (settings === undefined) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock aria-hidden="true" className="h-4 w-4" />
              Follow-Up Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-10 w-32" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail aria-hidden="true" className="h-4 w-4" />
              Gmail Labels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-6 w-full" />
          </CardContent>
        </Card>
      </>
    );
  }

  const followUpEnabled = settings?.followUpEnabled ?? true;
  const maxFollowUps = settings?.maxFollowUps ?? 3;
  const gmailLabelsEnabled = settings?.gmailLabelsEnabled ?? true;
  const displayMax = maxFollowUpsInput !== '' ? maxFollowUpsInput : String(maxFollowUps);

  const handleMaxFollowUpsBlur = () => {
    const parsed = parseInt(maxFollowUpsInput, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 5) {
      void onUpdate({ maxFollowUps: parsed });
    }
    setMaxFollowUpsInput('');
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock aria-hidden="true" className="h-4 w-4" />
            Follow-Up Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="follow-up-enabled" className="text-sm font-medium">
                  Enable Follow-Ups
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>Automatically send follow-up messages if the contact hasn't replied</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">
                Automatically send follow-up messages when contacts don&apos;t respond
              </p>
            </div>
            <Switch
              id="follow-up-enabled"
              checked={followUpEnabled}
              onCheckedChange={checked => void onUpdate({ followUpEnabled: checked })}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="max-follow-ups" className="text-sm font-medium">
                Max Follow-Ups per Contact
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>Maximum number of automatic follow-up messages per conversation (1-5)</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Input
              id="max-follow-ups"
              type="number"
              min={1}
              max={5}
              value={displayMax}
              disabled={!followUpEnabled}
              className="w-24"
              onChange={e => setMaxFollowUpsInput(e.target.value)}
              onBlur={handleMaxFollowUpsBlur}
            />
            <p className="text-xs text-muted-foreground">
              Between 1 and 5 follow-ups (1hr, 3hr, 9hr intervals)
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail aria-hidden="true" className="h-4 w-4" />
            Gmail Labels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="gmail-labels-enabled" className="text-sm font-medium">
                  Apply AI Labels to Gmail
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p>
                      Apply AI classification labels (e.g., AI/Insurance, AI/Property) to processed
                      emails
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">
                Classify Gmail threads with labels like AI/Insurance, AI/Property, etc.
              </p>
            </div>
            <Switch
              id="gmail-labels-enabled"
              checked={gmailLabelsEnabled}
              onCheckedChange={checked => void onUpdate({ gmailLabelsEnabled: checked })}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
